<template>
  <el-dialog
    v-model="visible"
    title="个人资料"
    width="500px"
    :before-close="handleClose"
  >
    <el-form
      ref="profileFormRef"
      :model="profileForm"
      :rules="profileRules"
      label-width="100px"
      class="profile-form"
    >
      <el-form-item label="用户名">
        <el-input v-model="profileForm.username" disabled />
      </el-form-item>
      
      <el-form-item label="用户类型">
        <el-tag :type="userTypeColor">{{ userTypeText }}</el-tag>
      </el-form-item>
      
      <el-form-item label="角色">
        <el-tag :type="roleColor">{{ roleText }}</el-tag>
      </el-form-item>
      
      <el-form-item label="真实姓名" prop="real_name">
        <el-input 
          v-model="profileForm.real_name" 
          placeholder="请输入真实姓名"
          maxlength="50"
          show-word-limit
        />
      </el-form-item>
      
      <el-form-item label="邮箱" prop="email">
        <el-input 
          v-model="profileForm.email" 
          placeholder="请输入邮箱地址"
          maxlength="100"
        />
      </el-form-item>
      
      <el-form-item label="手机号" prop="phone">
        <el-input 
          v-model="profileForm.phone" 
          placeholder="请输入手机号"
          maxlength="20"
        />
      </el-form-item>
      
      <el-form-item label="创建时间">
        <el-input v-model="createdAtText" disabled />
      </el-form-item>
      
      <el-form-item label="最后登录">
        <el-input v-model="lastLoginText" disabled />
      </el-form-item>
    </el-form>
    
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleSave"
          :loading="saving"
        >
          保存
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useStore } from 'vuex'
import { authAPI } from '@/services/api'

export default {
  name: 'ProfileDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const store = useStore()
    const profileFormRef = ref()
    const saving = ref(false)
    
    const visible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })

    const profileForm = reactive({
      username: '',
      real_name: '',
      email: '',
      phone: '',
      user_type: '',
      role: '',
      created_at: '',
      last_login_at: ''
    })
    
    const profileRules = {
      real_name: [
        { max: 50, message: '真实姓名不能超过50个字符', trigger: 'blur' }
      ],
      email: [
        { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' },
        { max: 100, message: '邮箱地址不能超过100个字符', trigger: 'blur' }
      ],
      phone: [
        { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
      ]
    }
    
    const userTypeText = computed(() => {
      return profileForm.user_type === 'admin' ? '管理员' : '代理商'
    })
    
    const userTypeColor = computed(() => {
      return profileForm.user_type === 'admin' ? 'danger' : 'warning'
    })
    
    const roleText = computed(() => {
      const roleMap = {
        'super_admin': '超级管理员',
        'admin': '管理员',
        'agent': '代理商'
      }
      return roleMap[profileForm.role] || profileForm.role
    })
    
    const roleColor = computed(() => {
      const colorMap = {
        'super_admin': 'danger',
        'admin': 'primary',
        'agent': 'success'
      }
      return colorMap[profileForm.role] || 'info'
    })
    
    const createdAtText = computed(() => {
      return profileForm.created_at ? 
        new Date(profileForm.created_at).toLocaleString('zh-CN') : ''
    })
    
    const lastLoginText = computed(() => {
      return profileForm.last_login_at ? 
        new Date(profileForm.last_login_at).toLocaleString('zh-CN') : '从未登录'
    })
    
    // 加载用户资料
    const loadProfile = async () => {
      try {
        const response = await authAPI.getProfile()
        if (response.data.success) {
          const data = response.data.data
          // 直接设置每个属性以确保响应式更新
          profileForm.username = data.username || ''
          profileForm.real_name = data.real_name || ''
          profileForm.email = data.email || ''
          profileForm.phone = data.phone || ''
          profileForm.user_type = data.type || ''
          profileForm.role = data.role || ''
          profileForm.created_at = data.created_at || ''
          profileForm.last_login_at = data.last_login_at || ''
        }
      } catch (error) {
        console.error('加载用户资料失败:', error)
        ElMessage.error('加载用户资料失败')
      }
    }
    
    // 保存用户资料
    const handleSave = async () => {
      try {
        await profileFormRef.value.validate()
        
        saving.value = true
        const updateData = {
          real_name: profileForm.real_name,
          email: profileForm.email,
          phone: profileForm.phone
        }
        
        const response = await authAPI.updateProfile(updateData)
        if (response.data.success) {
          ElMessage.success('个人资料更新成功')
          
          // 更新store中的用户信息
          await store.dispatch('auth/updateUserInfo', updateData)
          
          handleClose()
        } else {
          ElMessage.error(response.data.message || '更新失败')
        }
      } catch (error) {
        if (error !== false) { // 不是表单验证错误
          console.error('更新个人资料失败:', error)
          ElMessage.error('更新个人资料失败')
        }
      } finally {
        saving.value = false
      }
    }
    
    const handleClose = () => {
      visible.value = false
    }
    
    // 监听对话框打开，加载数据
    watch(visible, (newVal) => {
      if (newVal) {
        loadProfile()
      }
    })
    
    return {
      visible,
      profileFormRef,
      saving,
      profileForm,
      profileRules,
      userTypeText,
      userTypeColor,
      roleText,
      roleColor,
      createdAtText,
      lastLoginText,
      handleSave,
      handleClose
    }
  }
}
</script>

<style scoped>
.profile-form {
  padding: 0 20px;
}

.dialog-footer {
  text-align: right;
}

:deep(.el-form-item__label) {
  font-weight: 600;
}

:deep(.el-input.is-disabled .el-input__inner) {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
  color: #606266;
}
</style>
